import { memo, useCallback, useMemo, useRef, useState } from "react";
import { ActivityIndicator, ScrollView } from "react-native";
import { router, Stack, useLocalSearchParams } from "expo-router";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { View, Text, FormInput, Card, Button } from "@src/components";
import { Icon } from "@src/components/Icon";
import { SelectableUserList } from "@src/features/settings/components/SelectableUserList";
import { Section } from "@src/features/settings/components/Section";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { BottomSheetModal } from "@gorhom/bottom-sheet";
import { AssignRolesBottomSheet } from "@src/features/settings/components/AssignRolesBottomSheet";
import { AssignRolesUserList, type AssignRole } from "@src/features/settings/components/AssignRolesUserList";
import { FloatingSelectionBar } from "@src/features/settings/components/FloatingSelectionBar";
import { showSuccessToast, showErrorToast } from "@src/utils/toast";
import { useAccessManagement, useAddCameraPermission } from "@visioni/a3s-access/app";
import type { CameraRole, Role } from "@visioni/a3s-access/schemas";
import { useCameras } from "@src/features/cameras";
import { useSiteNames } from "@src/features/cameras/hooks/useSiteNames";
import { parseSiteRole } from "@src/features/settings/utils";
import type { UserWithSites } from "@src/features/settings/data/mock-all-users";
import { isWeb } from "@src/utils/web-helpers";
import { WebLayout } from "@src/components/web/WebLayout.web";
// @ts-expect-error baseline 2026-02-16
import { RightDrawer } from "@src/components/web";
import { formatCameraLabel } from "@src/utils/key-parser";

const AddUsersToCameraScreen = memo(() => {
  const { siteId, cameraKey } = useLocalSearchParams<{
    siteId: string;
    cameraKey: string;
  }>();
  const { theme } = useUnistyles();
  const { bottom } = useSafeAreaInsets();
  const bottomSheetRef = useRef<BottomSheetModal>(null);

  const cameraId = `${siteId}/${cameraKey}`;
  const cameraLabel = useMemo(() => formatCameraLabel(cameraKey) || cameraKey, [cameraKey]);

  const { data, isLoading } = useAccessManagement();
  const { data: cameras } = useCameras();
  const addCameraPermission = useAddCameraPermission();

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());

  // Web-only: RightDrawer state for the "assign roles" step. Native uses a BottomSheetModal instead.
  const [isAssignRolesDrawerVisible, setIsAssignRolesDrawerVisible] = useState(false);
  const [webRoleMap, setWebRoleMap] = useState<Map<string, AssignRole>>(new Map());

  const handleWebRoleChange = useCallback((userId: string, role: AssignRole) => {
    setWebRoleMap((prev) => {
      const next = new Map(prev);
      next.set(userId, role);
      return next;
    });
  }, []);

  const handleCloseAssignRolesDrawer = useCallback(() => {
    setIsAssignRolesDrawerVisible(false);
  }, []);

  const siteNamesMap = useSiteNames();
  const siteNameById = useMemo(() => {
    const map = new Map<string, string>();
    if (!cameras) return map;
    for (const c of cameras) {
      if (!map.has(c.site.id)) map.set(c.site.id, siteNamesMap?.get(c.site.id) ?? c.site.id);
    }
    return map;
  }, [cameras, siteNamesMap]);

  const currentSiteName = siteId ? (siteNameById.get(siteId) ?? siteId) : "";

  // Users already granted a camera-level permission on THIS camera: exclude from both sections.
  const usersOnCamera = useMemo(() => {
    if (!data || !cameraId) return new Set<string>();
    return new Set(data.cameras.filter((a) => a.camera === cameraId).map((a) => String(a.user)));
  }, [data, cameraId]);

  // Users are bucketed by whether they have a site role on the camera's site or on other sites.
  const { currentSiteUsers, otherSiteUsers } = useMemo<{
    currentSiteUsers: UserWithSites[];
    otherSiteUsers: UserWithSites[];
  }>(() => {
    if (!data || !siteId) return { currentSiteUsers: [], otherSiteUsers: [] };

    const userById = new Map(data.users.map((u) => [String(u.id), u]));

    const onCurrentSite = new Map<string, UserWithSites>();
    const onOtherSites = new Map<string, UserWithSites>();

    for (const r of data.roles) {
      const parsed = parseSiteRole(r.role as Role);
      if (!parsed || parsed.siteId === "*") continue;

      const userId = String(r.user);
      if (usersOnCamera.has(userId)) continue;

      const u = userById.get(userId);
      if (!u) continue;

      if (parsed.siteId === siteId) {
        if (!onCurrentSite.has(userId)) {
          onCurrentSite.set(userId, {
            id: userId,
            name: [u.givenName, u.familyName].filter(Boolean).join(" ") || u.email,
            email: u.email,
            phoneNumber: u.phoneNumber ?? "",
            siteNames: [],
          });
        }
      } else {
        const siteName = siteNameById.get(parsed.siteId) ?? parsed.siteId;
        const existing = onOtherSites.get(userId);
        if (existing) {
          if (!existing.siteNames.includes(siteName)) existing.siteNames.push(siteName);
        } else {
          onOtherSites.set(userId, {
            id: userId,
            name: [u.givenName, u.familyName].filter(Boolean).join(" ") || u.email,
            email: u.email,
            phoneNumber: u.phoneNumber ?? "",
            siteNames: [siteName],
          });
        }
      }
    }

    // A user on the current site may also belong to other sites: drop them from "other" bucket.
    for (const id of onCurrentSite.keys()) onOtherSites.delete(id);

    return {
      currentSiteUsers: Array.from(onCurrentSite.values()),
      otherSiteUsers: Array.from(onOtherSites.values()),
    };
  }, [data, siteId, siteNameById, usersOnCamera]);

  const filterByQuery = useCallback(
    (users: UserWithSites[]) => {
      const q = searchQuery.trim().toLowerCase();
      if (!q) return users;
      return users.filter((u) => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q));
    },
    [searchQuery],
  );

  const filteredCurrentSiteUsers = useMemo(() => filterByQuery(currentSiteUsers), [currentSiteUsers, filterByQuery]);
  const filteredOtherSiteUsers = useMemo(() => filterByQuery(otherSiteUsers), [otherSiteUsers, filterByQuery]);

  const selectedUsers = useMemo(() => {
    const all = [...currentSiteUsers, ...otherSiteUsers];
    return all.filter((u) => selectedUserIds.has(u.id));
  }, [currentSiteUsers, otherSiteUsers, selectedUserIds]);

  const handleToggleUser = useCallback((userId: string) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  }, []);

  const handleOpenAssignRoles = useCallback(() => {
    if (isWeb) {
      setWebRoleMap(new Map(selectedUsers.map((u) => [u.id, "viewer" as AssignRole])));
      setIsAssignRolesDrawerVisible(true);
      return;
    }
    bottomSheetRef.current?.present();
  }, [selectedUsers]);

  const handleSaveRoles = useCallback(
    async (assignments: { userId: string; role: "manager" | "viewer" }[]) => {
      if (!cameraId) return;
      try {
        await Promise.all(
          assignments.map((a) => {
            const role: CameraRole = a.role === "manager" ? "camera_manager" : "camera_viewer";
            return addCameraPermission.mutateAsync({ user: a.userId, camera: cameraId, role });
          }),
        );
        bottomSheetRef.current?.dismiss();
        setTimeout(() => {
          showSuccessToast({ message: "Users added to camera" });
          router.back();
        }, 300);
      } catch (error) {
        showErrorToast({ message: "Failed to add users to camera" });
        throw error;
      }
    },
    [cameraId, addCameraPermission],
  );

  const handleWebSaveRoles = useCallback(async () => {
    const assignments = selectedUsers.map((user) => ({
      userId: user.id,
      role: webRoleMap.get(user.id) ?? ("viewer" as AssignRole),
    }));
    try {
      await handleSaveRoles(assignments);
    } catch {
      return;
    }
    setIsAssignRolesDrawerVisible(false);
  }, [selectedUsers, webRoleMap, handleSaveRoles]);

  if (isLoading) {
    if (isWeb) {
      return (
        <>
          <Stack.Screen options={{ headerShown: false }} />
          <WebLayout>
            <View style={styles.centered}>
              <ActivityIndicator />
            </View>
          </WebLayout>
        </>
      );
    }
    return (
      <>
        <Stack.Screen
          options={{
            title: `Add users to ${cameraLabel}`,
            headerBackButtonDisplayMode: "minimal",
            headerStyle: { backgroundColor: theme.colors.background },
          }}
        />
        <View style={styles.centered}>
          <ActivityIndicator />
        </View>
      </>
    );
  }

  if (isWeb) {
    return (
      <>
        <Stack.Screen options={{ headerShown: false }} />
        <WebLayout>
          <ScrollView style={styles.scrollView} contentContainerStyle={styles.contentContainerStyle}>
            {/* Web-only header surfacing the destination camera. The native screen tucks this
                  into the navigation bar via `Stack.Screen` title; on web the bar is hidden. */}
            <View flexDirection="row" alignItems="center" justifyContent="space-between" mb="3xl">
              <Text textStyle="lg/semibold" color="text">
                Add users to {cameraLabel}
              </Text>
            </View>

            <View gap="3xl" flex={1} flexDirection="row" alignItems="flex-start">
              <Card style={{ flexBasis: "50%" }}>
                <View mb="xl" gap="2xl">
                  <Text textStyle="xs/regular" color="text">
                    Search for existing users
                  </Text>
                  <FormInput
                    placeholder="Enter name or email"
                    value={searchQuery}
                    onChangeText={setSearchQuery}
                    iconLeft={<Icon name="search" color={theme.colors.textMuted} />}
                    clearable
                    size="medium"
                    containerStyle={styles.formInput}
                  />
                </View>
                <Section
                  title={`${currentSiteName} users`}
                  titleTextVariant="xs/regular"
                  titleColor="textMuted"
                  style={styles.section}
                >
                  <SelectableUserList
                    users={filteredCurrentSiteUsers}
                    selectedUserIds={selectedUserIds}
                    onToggleUser={handleToggleUser}
                  />
                </Section>
              </Card>

              <Card style={{ flexBasis: "50%" }}>
                <Section
                  title="Other site users"
                  subtitle="Users with access to other sites in this organisation."
                  titleTextVariant="xs/regular"
                  titleColor="textMuted"
                >
                  <SelectableUserList
                    users={filteredOtherSiteUsers}
                    selectedUserIds={selectedUserIds}
                    onToggleUser={handleToggleUser}
                  />
                </Section>
              </Card>
            </View>
          </ScrollView>
        </WebLayout>
        <FloatingSelectionBar
          selectedCount={selectedUserIds.size}
          actionLabel="Add to camera"
          onAction={handleOpenAssignRoles}
          bottomInset={bottom}
          style={styles.floatingSelectionBar}
        />
        <RightDrawer
          width={450}
          visible={isAssignRolesDrawerVisible}
          onClose={handleCloseAssignRolesDrawer}
          title={`Assign roles for ${cameraLabel}`}
          footer={<Button variant="primary" label="Save" size="md" onPress={handleWebSaveRoles} />}
        >
          <AssignRolesUserList users={selectedUsers} roleMap={webRoleMap} onRoleChange={handleWebRoleChange} />
        </RightDrawer>
      </>
    );
  }

  return (
    <>
      <Stack.Screen
        options={{
          title: `Add users to ${cameraLabel}`,
          headerBackButtonDisplayMode: "minimal",
          headerStyle: { backgroundColor: theme.colors.background },
        }}
      />
      <ScrollView style={styles.scrollView} contentContainerStyle={styles.contentContainerStyle}>
        <View mb="3xl" gap="2xl">
          <Text textStyle="xs/regular" color="text">
            Create new or search for existing users
          </Text>
          <FormInput
            placeholder="Enter name or email"
            value={searchQuery}
            onChangeText={setSearchQuery}
            iconLeft={<Icon name="search" color={theme.colors.textMuted} />}
            clearable
            size="medium"
            containerStyle={styles.formInput}
          />
        </View>

        <Section
          title={`${currentSiteName} users`}
          titleTextVariant="xs/regular"
          titleColor="textMuted"
          style={styles.section}
        >
          <SelectableUserList
            users={filteredCurrentSiteUsers}
            selectedUserIds={selectedUserIds}
            onToggleUser={handleToggleUser}
          />
        </Section>

        <Section title="Other site users" titleTextVariant="xs/regular" titleColor="textMuted" style={styles.section}>
          <SelectableUserList
            users={filteredOtherSiteUsers}
            selectedUserIds={selectedUserIds}
            onToggleUser={handleToggleUser}
          />
        </Section>
      </ScrollView>
      <FloatingSelectionBar
        selectedCount={selectedUserIds.size}
        actionLabel="Add to camera"
        onAction={handleOpenAssignRoles}
        bottomInset={bottom}
      />
      <AssignRolesBottomSheet
        bottomSheetRef={bottomSheetRef}
        selectedUsers={selectedUsers}
        targetName={cameraLabel}
        onSave={handleSaveRoles}
      />
    </>
  );
});

export default AddUsersToCameraScreen;

const styles = StyleSheet.create((theme) => ({
  scrollView: {
    flex: 1,
  },
  contentContainerStyle: {
    flexGrow: 1,
    padding: theme.spacing["3xl"],
    paddingBottom: theme.spacing["12xl"],
  },
  centered: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  formInput: {
    backgroundColor: theme.colors.surfaceCard,
    borderColor: theme.colors.field.border_default,
  },
  section: {
    marginTop: theme.spacing["4xl"],
  },
  floatingSelectionBar: {
    width: 350,
    left: "50%",
    right: "auto",
  },
}));
